import {EventEmitter, Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {BehaviorSubject, Observable} from "rxjs";
import {Article} from "../structure/types/article.class";
import {StorageService} from "./storage.service";
import {NavController} from "@ionic/angular";

@Injectable({
    providedIn: 'root'
})
export class UserService {

    /**
     * Saved Articles Subscriber
     */
    _savedArticles: any[] = [];
    _savedArticles$ = new BehaviorSubject<any[]>(this._savedArticles);
    private savedArticlesLoaded = false;

    set savedArticles(value: any[]) {
        this._savedArticles = Array.isArray(value) ? value : [];
        this._savedArticles$.next(this._savedArticles);
        this.savedArticlesLoaded = true;
    }

    get savedArticles(): any {
        return this._savedArticles$.asObservable();
    }

    get savedArticlesValue(): any[] {
        return this._savedArticles$.getValue();
    }

    saveArticleEvent: EventEmitter<void> = new EventEmitter<void>();
    removeArticleEvent: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Saved Guides Subscriber
     */
    _savedGuides: any[] = [];
    _savedGuides$ = new BehaviorSubject<any[]>(this._savedGuides);
    private savedGuidesLoaded = false;

    set savedGuides(value: any[]) {
        this._savedGuides = Array.isArray(value) ? value : [];
        this._savedGuides$.next(this._savedGuides);
        this.savedGuidesLoaded = true;
    }

    get savedGuides():any {
        return this._savedGuides$.asObservable();
    }

    get savedGuidesValue(): any[] {
        return this._savedGuides$.getValue();
    }

    saveGuideEvent: EventEmitter<void> = new EventEmitter<void>();
    removeGuideEvent: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Saved Shorts Subscriber
     */
    _savedShorts: any[] = [];
    _savedShorts$ = new BehaviorSubject<any[]>(this._savedShorts);
    private savedShortsLoaded = false;

    set savedShorts(value: any[]) {
        this._savedShorts = Array.isArray(value) ? value : [];
        this._savedShorts$.next(this._savedShorts);
        this.savedShortsLoaded = true;
    }

    get savedShorts(): any {
        return this._savedShorts$.asObservable();
    }

    get savedShortsValue(): any[] {
        return this._savedShorts$.getValue();
    }

    saveShortEvent: EventEmitter<void> = new EventEmitter<void>();
    removeShortEvent: EventEmitter<void> = new EventEmitter<void>();

    /**
     * User Profile Subscriber
     */
    _data: any = {}
    _data$ = new BehaviorSubject(this._data);

    set data(value: Observable<any>) {
        this._data = value;
        this._data$.next(this._data);
    }

    get data() {
        return this._data$.asObservable()
    }


    isLoggedIn: boolean = false;

    constructor(
        private apiService: ApiService,
        private storageService: StorageService,
        private navController: NavController,
    ) {
    }

    async init() {
        const token = await this.storageService.get('token');

        if (!token) {
            this.isLoggedIn = false;
            return;
        }

        try {
            const user = await this.getAccount();
            if (user?.do_risk_assessment) {
                await this.navController.navigateRoot([`/risk-assesment/${user.tag}`]);
            }
        } catch (error) {
            this.isLoggedIn = false;
        }
    }

    doLogin(params: any) {
        return this.apiService.doRequest('member/login', 'POST', params);
    }

    twoFactorLogin(params: any) {
        return this.apiService.doRequest('member/two-factor', 'POST', params);
    }

    /**
     * Send Forgot Password Code
     * @param email
     * @return true always
     */
    sendForgotCode(email: string) {
        let params = {
            email: email,
            action: "forgot"
        }
        return this.apiService.doRequest('member/forgot', 'POST', params);
    }

    /**
     * Reset password after Forgot Form Submit
     * @param email
     * @param password
     * @param code
     */
    resetPassword(email: string, password: string, code: string) {
        let params = {
            email: email,
            password: password,
            code: code
        }
        return this.apiService.doRequest('member/forgot/two-factor', 'POST', params);
    }


    /**
     * Do Register Request for Member
     * @param params
     */
    register(params: any) {
        return this.apiService.doRequest('member/register', 'POST', params)
    }

    /**
     * Do Register Request for Member
     * @param params
     */
    registerStep1(params: any) {
        return this.apiService.doRequest('member/register/urn', 'POST', params)
    }

    /**
     * Do Register Request for Member
     * @param params
     */
    registerStep2(params: any) {
        return this.apiService.doRequest('member/register/urn-confirm', 'POST', params)
    }

    /**
     * Do Register Request for Member
     * @param password
     * @param newPassword
     */
    changePassword(password: string, newPassword: string) {
        let params = {
            password: password,
            new_password: newPassword,
        }
        return this.apiService.doRequest('my-account/update-password', 'POST', params)
    }

    /**
     *
     * @param params
     */
    updateAccount(params: any) {
        return this.apiService.doRequest('my-account/edit', 'POST', params)
    }

    /**
     * Save the "complete your details" fields (registration_type + crime types)
     * from the in-app modal.
     * @param params
     */
    updateSupportDetails(params: any) {
        return this.apiService.doRequest('my-account/support-details', 'POST', params)
    }

    toggleCrisisMode() {
        return this.apiService.doRequest('my-account/toggle-crisis-mode', 'POST', []);
    }


    /**
     * Logout from account
     */
    logout() {
        return this.apiService.doRequest('member/logout', 'POST', {})
    }


    /**
     * Do Save Article Request for Member
     * @param articleId
     */
    saveArticle(articleId: number) {
        let params = {
            article_id: articleId,
        }
        return this.apiService.doRequest('favorite-articles', 'POST', params)
    }

    /**
     * Do Remove Saved Article Request for Member
     * @param articleId
     */
    removeArticle(articleId: number) {
        return this.apiService.doRequest('favorite-articles/' + articleId, 'DELETE', [])
    }

    /**
     * Get Saved Articles Request
     */
    getSavedArticles() {
        return this.apiService.doRequest('favorite-articles', 'GET', [])
    }

    /**
     * Get Saved Articles Data
     */
    async getSavedArticlesData(forceRefresh: boolean = false) {
        if (!forceRefresh && this.savedArticlesLoaded) {
            return this.savedArticlesValue;
        }

        let items: any[] = [];

        const response = await this.getSavedArticles();
        const itemsResponse: Article[] = response.data;

        if (Array.isArray(itemsResponse) && itemsResponse.length > 0) {
            items = itemsResponse;
        }

        this.savedArticles = items;
        return items;
    }

    /**
     * Do Save Guide Request for Member
     */
    saveGuide(guideId: number) {
        const params = {
            guide_id: guideId,
        };
        return this.apiService.doRequest('favorite-guides', 'POST', params);
    }

    /**
     * Do Remove Saved Guide Request for Member
     */
    removeGuide(guideId: number) {
        return this.apiService.doRequest('favorite-guides/' + guideId, 'DELETE', []);
    }

    /**
     * Get Saved Guides Request
     */
    getSavedGuides() {
        return this.apiService.doRequest('favorite-guides', 'GET', []);
    }

    /**
     * Get Saved Guides Data
     * forceRefresh = true => always call API
     * forceRefresh = false => use cache if already loaded
     */
    async getSavedGuidesData(forceRefresh: boolean = false) {
        if (!forceRefresh && this.savedGuidesLoaded) {
            return this.savedGuidesValue;
        }

        let items: any[] = [];

        const response = await this.getSavedGuides();
        const itemsResponse = response.data;

        if (Array.isArray(itemsResponse) && itemsResponse.length > 0) {
            items = itemsResponse;
        }

        this.savedGuides = items;
        return items;
    }

    /**
     * Do Save Short Request for Member
     * @param shortId
     */
    saveShort(shortId: number) {
        const params = { short_id: shortId };
        return this.apiService.doRequest('favorite-shorts', 'POST', params);
    }

    /**
     * Do Remove Saved Short Request for Member
     * @param shortId
     */
    removeShort(shortId: number) {
        return this.apiService.doRequest('favorite-shorts/' + shortId, 'DELETE', []);
    }

    /**
     * Get Saved Shorts Request
     */
    getSavedShorts() {
        return this.apiService.doRequest('favorite-shorts', 'GET', []);
    }

    /**
     * Get Saved Shorts Data
     */
    async getSavedShortsData(forceRefresh: boolean = false) {
        if (!forceRefresh && this.savedShortsLoaded) {
            return this.savedShortsValue;
        }

        let items: any[] = [];

        const response = await this.getSavedShorts();
        const itemsResponse = response.data;

        if (Array.isArray(itemsResponse) && itemsResponse.length > 0) {
            items = itemsResponse;
        }

        this.savedShorts = items;
        return items;
    }

    /**
     * Get Current Account details
     * @param reset
     */
    async getAccount(reset: boolean = false) {
        try {
            const accountDetailsResponse = await this.getAccountDetails();
            this.data = accountDetailsResponse.data;

            if (Object.keys(accountDetailsResponse.data).length) {
                this.isLoggedIn = true;
                await this.storageService.set('lang', accountDetailsResponse.data.locale ?? 'en');
            } else {
                this.isLoggedIn = false;
            }

            return accountDetailsResponse.data;
        } catch (error) {
            this.isLoggedIn = false;
            throw error;
        }
    }

    /**
     * Get account details
     */
    getAccountDetails() {
        return this.apiService.doRequest('my-account', 'GET', [])
    }

    /**
     * In-app notifications
     */
    getNotifications(page: number = 1) {
        return this.apiService.doRequest('notifications', 'GET', {page})
    }

    markNotificationRead(notificationId: number) {
        return this.apiService.doRequest('notifications/' + notificationId + '/read', 'POST', {})
    }

    markAllNotificationsRead() {
        return this.apiService.doRequest('notifications/read-all', 'POST', {})
    }

    deleteNotification(notificationId: number) {
        return this.apiService.doRequest('notifications/' + notificationId, 'DELETE', [])
    }

    deleteAllNotifications() {
        return this.apiService.doRequest('notifications', 'DELETE', [])
    }

    /**
     * Set Two Factor Method for Account
     * @param type
     */
    twoFactorMethod(type: string) {
        let params = {
            two_factor_method: type,
        }
        return this.apiService.doRequest('my-account/update-two-factor', 'POST', params)
    }


    /**
     * Do Delete My Account Request
     */
    deleteAccount() {
        return this.apiService.doRequest('my-account/delete', 'DELETE', [])
    }


    /**
     * Do Send Code Request for Member
     */
    sendCode(params: any) {
        return this.apiService.doRequest('member/send-two-factor', 'POST', params)
    }

    /**
     * Verify Code
     * @param type
     * @param code
     * @param temporaryToken
     */
    verifyCode(type: string = "email", code: string, temporaryToken: string) {
        const params = {
            code,
            type,
            temporaryToken
        };
        return this.apiService.doRequest('member/validateAccount', 'POST', params);
    }

    /**
     * Partially update account only "email" and "phone" are allowed
     * @param key
     * @param value
     * @param temporaryToken
     */
    updateAccountField(key: string, value: string, temporaryToken: string) {
        let params = {
            temporaryToken: temporaryToken,
            key: key,
            value: value
        }
        return this.apiService.doRequest('member/updateField', 'POST', params)
    }

    updateLocale() {
        return this.apiService.doRequest('member/updateLocale', 'GET', [])
    }

    updateSkipRa() {
        return this.apiService.doRequest('member/updateSkipRa', 'GET', [])
    }


    /**
     * Get Account Data By Temporary Token
     * @param temporaryToken
     */
    getAccountByTemporaryToken(temporaryToken: string) {
        return this.apiService.doRequest('member/getByTemporaryToken', 'POST', {temporaryToken})
    }


    /**
     *
     * @param params
     */
    addEntry(params: any) {
        return this.apiService.doRequest('member/entry', 'POST', params)
    }

    /**
     * @param entryId
     * @param params
     */
    editEntry(entryId: any, params: any) {
        return this.apiService.doRequest('member/entry/' + entryId, 'PATCH', params)
    }


    /**
     * Do Remove Entry Request for Member
     * @param entryId
     */
    removeEntry(entryId: number) {
        return this.apiService.doRequest('member/entry/' + entryId, 'DELETE', [])
    }

    /**
     * Do Submit Assessment Request for Member
     * @param params
     */
    submitRiskAssessment(params: any) {
        return this.apiService.doRequest('risk-assessment', 'POST', params)
    }

    /**
     * Do Submit Assessment Request for Member
     * @param params
     */
    submitRiskAssessmentInitialQuestions(params: any) {
        return this.apiService.doRequest('risk-assessment/initial-questions', 'POST', params)
    }

    getMyData() {
        return this.apiService.doRequest('member/getMyData', 'GET', [])
    }


    getDownloadData() {
        return this.apiService.doRequest('my-account/export-data-text', 'GET', [])
    }

    updateNotificationSettings(payload: {
        push_notifications_enabled: boolean;
        live_incidents_enabled: boolean;
    }) {
        return this.apiService.doRequest('my-account/update-notification-settings', 'POST' ,payload);
    }

}
